import { mapGetters, mapState } from 'vuex';
import ApiService from '@/services/apiService';
import NiotaClassicApiService from '@/services/niotaClassicApiService';
import get from 'lodash/get';

export default {
  data() {
    return {
      accountFilterReady: false,
      selectedAccounts: [],

      searchOperator: 'AND',
      searchItems: [],
      searchGroups: [],
      searchTags: [],
      bridgeTypes: [],
      deviceTypes: [],
      deviceDrivers: [],
      deviceTemplates: [],
      ddrKeys: [],
      ddrValues: [],
    };
  },
  computed: {
    ...mapState('app', ['language']),
    ...mapGetters({ isFeatureFlagEnabled: 'app/isFeatureFlagEnabled' }),
    searchTypes() {
      const searchTypes = [
        {
          text: this.$i18n_t('DigitalTwin.VirtualDevice.props.quickSearch'),
          param: 'quickSearch',
        },
        {
          text: this.$i18n_t('DigitalTwin.VirtualDevice.props.title'),
          param: 'title',
        },
        {
          text: this.$i18n_t('DigitalTwin.VirtualDevice.props.group'),
          param: 'group',
          searchHandler: async (searchFor, page, pageSize) => {
            const result = await this.searchForGroups(searchFor, page, pageSize);
            return {
              totalCount: result.totalCount,
              items: result.groupList,
            };
          },
        },
        {
          text: this.$i18n_t('DigitalTwin.VirtualDevice.props.tag'),
          param: 'tag',
          searchHandler: async (searchFor, page, pageSize) => {
            const result = await this.searchForTags(searchFor, page, pageSize);
            return {
              totalCount: result.totalCount,
              items: result.tagList,
            };
          },
        },
        {
          text: this.$i18n_t('DigitalTwin.VirtualDevice.props.referenceId'),
          param: 'ddrTarget',
          searchHandler: async (_, page, pageSize, entityType, key) => {
            const result = await this.searchForDdrSettings(page, pageSize, entityType, key);
            return {
              totalCount: result.totalcount,
              items: result.entities,
            };
          },
        },
        {
          text: this.$i18n_t('DigitalTwin.VirtualDevice.props.keyValue'),
          param: 'keyValue',
        },
        {
          text: this.$i18n_t('DigitalTwin.VirtualDevice.props')['bridge.type'],
          param: 'bridge.type',
          searchHandler: async () => {
            const result = await this.searchForConnectorTypes();
            return {
              totalCount: result.length,
              items: result,
            };
          },
        },
        this.isFeatureFlagEnabled('env_dispatcher_devicetype_fallback')
          ? {
              text: this.$i18n_t('DigitalTwin.VirtualDevice.props.deviceType'),
              param: 'deviceType',
              searchHandler: async () => {
                const result = await this.searchForDeviceTypes();
                return {
                  totalCount: result.length,
                  items: result,
                };
              },
            }
          : {
              text: this.$i18n_t('DigitalTwin.VirtualDevice.props.deviceDriver'),
              param: 'deviceDriver',
              searchHandler: async (searchFor, page, pageSize) => {
                const result = await this.searchForDeviceDrivers(searchFor, page, pageSize);
                return {
                  totalCount: result.totalCount,
                  items: result.deviceDrivers,
                };
              },
            },
        {
          text: this.$i18n_t('DigitalTwin.VirtualDevice.props.deviceTemplate'),
          param: 'deviceTemplate',
          searchHandler: async (searchFor, page, pageSize) => {
            // pages for device templates fetch start from 1, not from 0
            page++;
            const result = await this.searchForDeviceTemplates(searchFor, page, pageSize);
            return {
              totalCount: result.pagination['total-count'],
              items: result.deviceTemplates,
            };
          },
        },
        {
          text: this.$i18n_t('DigitalTwin.VirtualDevice.props.deviceId'),
          param: 'deviceId',
        },
        {
          text: this.$i18n_t('Common.id'),
          param: 'id',
        },
        {
          text: this.$i18n_t('Common.digitalTwins.searchParentLabel'),
          param: 'parentId',
        },
        {
          text: this.$i18n_t('DigitalTwin.VirtualDevice.props.operationalStatus'),
          param: 'operationalStatus',
          searchHandler: () => {
            const result = this.operationalStatusItems;
            return {
              totalCount: result.length,
              items: result,
            };
          },
        },
        {
          text: this.$i18n_t('DigitalTwin.VirtualDevice.props.health'),
          param: 'health',
          searchHandler: () => {
            const result = this.healthStatusItems;
            return {
              totalCount: result.length,
              items: result,
            };
          },
        },
        {
          text: this.$i18n_t('DigitalTwin.VirtualDevice.props.address'),
          param: 'address',
        },
      ];

      return searchTypes;
    },
    statisticsSearchTypes() {
      return this.searchTypes.filter((st) => st.param !== 'zone');
    },
    operationalStatus() {
      return {
        0: this.$i18n_t('NiotaClassic.Devices.form.unset'),
        1: this.$i18n_t('NiotaClassic.Devices.form.inTransit'),
        2: this.$i18n_t('NiotaClassic.Devices.form.readyForOperation'),
        3: this.$i18n_t('NiotaClassic.Devices.form.inOperation'),
        4: this.$i18n_t('NiotaClassic.Devices.form.temporaryInactive'),
        5: this.$i18n_t('NiotaClassic.Devices.form.decommissioned'),
      };
    },
    operationalStatusItems() {
      return Object.keys(this.operationalStatus).map((status) => {
        return { text: this.operationalStatus[status], value: status };
      });
    },
  },
  watch: {
    language() {
      (this.searchTypes.find((item) => item.param === 'operationalStatus') || {}).items = this.operationalStatusItems;
      (this.searchTypes.find((item) => item.param === 'health') || {}).items = this.healthStatusItems;
      (this.searchTypes.find((item) => item.param === 'group') || {}).items = this.searchGroups;
      (this.searchTypes.find((item) => item.param === 'tag') || {}).items = this.searchTags;
      if (this.isFeatureFlagEnabled('env_dispatcher_devicetype_fallback')) {
        (this.searchTypes.find((item) => item.param === 'deviceType') || {}).items = this.deviceTypes;
      }
      (this.searchTypes.find((item) => item.param === 'deviceDriver') || {}).items = this.deviceDrivers;
      (this.searchTypes.find((item) => item.param === 'deviceTemplate') || {}).items = this.deviceTemplates;
      (this.searchTypes.find((item) => item.param === 'ddrTarget') || {}).items = this.ddrKeys = this.ddrValues;
      (this.searchTypes.find((item) => item.param === 'bridge.type') || {}).items = this.bridgeTypes;
    },
  },
  methods: {
    async searchForDeviceTypes() {
      if (!this.isFeatureFlagEnabled('env_n1_enabled')) {
        return [];
      }
      const result = await NiotaClassicApiService.deviceTypesGetPlain();
      return result.data.devicetypes.map((dt) => {
        return { value: dt.id, text: dt.title, id: dt.id };
      });
    },
    async searchForConnectorTypes() {
      const result = await ApiService.getBridgeTypes();
      const types = result
        .filter((bridge) => {
          // this bridge-type must have a create-functionality
          // otherwise it should have a read to get 1 single existing device as import
          if (
            get(bridge, 'manifest.functionScope.extension.devices.create') ||
            get(bridge, 'manifest.functionScope.extension.devices.read')
          ) {
            return true;
          }
          return false;
        })
        .map((bridge) => bridge.manifest.displayName);

      return types.map((type) => {
        return { value: type, text: type };
      });
    },
    async searchForTags() {
      const result = await ApiService.virtualDeviceFetchTags();
      result.tagList = result.tagList.map((tag) => {
        return { value: tag, text: tag };
      });

      return result;
    },
    async searchForGroups() {
      const result = await ApiService.virtualDeviceFetchGroups(this, null);
      result.groupList = result.groupList.map((group) => {
        return { value: group, text: group };
      });

      return result;
    },
    async searchForDeviceDrivers(search, page, pageSize) {
      const result = await ApiService.virtualDeviceDrivers.fetch({
        fields: 'id,title',
        offset: page * pageSize,
        limit: pageSize,
        search,
        sortBy: 'title',
      });
      result.deviceDrivers = result.deviceDrivers.map((dt) => {
        return { value: dt.id, text: dt.title, id: dt.id };
      });
      return result;
    },
    async searchForDdrSettings(page, pageSize, entityType, key) {
      const result = await ApiService.fetchDdrSettings({
        entityType,
        offset: page * pageSize,
        limit: pageSize,
        key,
        accounts: (this.selectedAccounts || []).map((acc) => acc.id),
      });
      result.entities = result.entities.map((e) => {
        return { value: e, text: e };
      });
      return result;
    },

    async searchForDeviceTemplates(search, page, pageSize) {
      const searchItems = search ? [{ variable: 'title', search }] : [];
      const result = await ApiService.deviceTemplatesFetch({
        tenantIds: (this.selectedAccounts || []).map((acc) => acc.id),
        page: page,
        pageSize,
        searchItems,
      });
      result.deviceTemplates = result.data.map((dt) => {
        return { value: dt.ProductiveVersion.id, text: dt.ProductiveVersion.title, id: dt.ProductiveVersion.id };
      });
      return result;
    },
  },
};
